import { Stack, Tooltip, Typography } from "@mui/material";
import { capitalize } from "lodash-es";
import type { FC } from "react";
import { MomentSegmentPreview } from "../../../../../../../segment/components/manager-v2/moment-segment-preview/moment-segment-preview";
import type { PreviewConfig } from "../../../../../../../segment/components/manager-v2/moment-segment-preview/types";
import type { MomentRecipientsClassify } from "../../../../../../hooks/use-moment-recipients-classify";
import { NotSet } from "../../components/not-set";
import { usePreviewContext } from "../../preview-context";

export const PreviewDetailsSidebarRecipients: FC = () => {
  const { preview_info, moment_preview_data: moment } = usePreviewContext();

  if (!preview_info.data?.classifyData || preview_info.data.classifyData.type === "unknown")
    return (
      <Stack direction="column" spacing={1}>
        <NotSet />
      </Stack>
    );

  const { type, data } = preview_info.data.classifyData;
  const additionalRecipients = moment.additional_recipients ?? [];
  const segment = moment.segment?.segment_json ?? null;

  const previewConfig: PreviewConfig | null = moment.target_channel
    ? {
        type: "channel",
        data: {
          channels: additionalRecipients.length,
          segment,
          channel_ids: additionalRecipients,
        },
      }
    : !segment
      ? null
      : moment.only_additional_recipients
        ? {
            type: "only_additional_recipients",
            data: {
              segment,
              additional_recipients: additionalRecipients.length,
              additional_recipient_ids: additionalRecipients,
            },
          }
        : moment.target_connection_type
          ? {
              type: "connection",
              data: { segment, target_connection_type: moment.target_connection_type },
            }
          : moment.target_manager
            ? { type: "manager", data: { segment } }
            : { type: "segment", data: { segment } };

  return (
    <Stack direction="column" spacing={1}>
      {type === "channel_notify" && <ChannelNotify data={data} />}
      {type === "channel" && <Channel data={data} />}
      {type === "notify" && <Notify data={data} />}
      {type === "connection" && <Connection data={data} />}
      {type === "recipients" && <Recipients data={data} />}
      {previewConfig && (
        <MomentSegmentPreview
          channel={moment.channel}
          preview_config={previewConfig}
          alignItems="flex-start"
        />
      )}
    </Stack>
  );
};

const ChannelNotify: FC<{
  data: Extract<MomentRecipientsClassify, { type: "channel_notify" }>["data"];
}> = ({ data }) => {
  const channelLabel = data.channel_name ? `#${data.channel_name}` : "Not Set";
  return <Typography variant="body2">{`${channelLabel} about ${data.segment_name}`}</Typography>;
};

const Channel: FC<{
  data: Extract<MomentRecipientsClassify, { type: "channel" }>["data"];
}> = ({ data }) => {
  if (!data.channel_name) return <NotSet />;
  return <Typography variant="body2">{`#${data.channel_name}`}</Typography>;
};

const Notify: FC<{
  data: Extract<MomentRecipientsClassify, { type: "notify" }>["data"];
}> = ({ data }) => {
  const prefix =
    data.recipients.length === 0
      ? "Not Set"
      : data.recipients.length > 1
        ? `${data.recipients.length} recipients`
        : data.recipients[0];

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="body2">{`${prefix} about ${data.segment_name}`}</Typography>
      <CcBcc cc={data.cc} bcc={data.bcc} />
    </Stack>
  );
};

const Connection: FC<{
  data: Extract<MomentRecipientsClassify, { type: "connection" }>["data"];
}> = ({ data }) => {
  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="body2">{`${capitalize(data.connection_type)} of ${data.segment_name}`}</Typography>
      <CcBcc cc={data.cc} bcc={data.bcc} />
    </Stack>
  );
};

const Recipients: FC<{
  data: Extract<MomentRecipientsClassify, { type: "recipients" }>["data"];
}> = ({ data }) => {
  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="body2">{data.segment_name}</Typography>
      <CcBcc cc={data.cc} bcc={data.bcc} />
    </Stack>
  );
};

const CcBcc: FC<{ cc: string[]; bcc: string[] }> = ({ cc, bcc }) => {
  if (cc.length === 0 && bcc.length === 0) return null;

  return (
    <Stack direction="row" spacing={1}>
      {cc.length > 0 && (
        <Tooltip title={cc.join(", ")}>
          <Typography variant="body2">+CC</Typography>
        </Tooltip>
      )}
      {bcc.length > 0 && (
        <Tooltip title={bcc.join(", ")}>
          <Typography variant="body2">+BCC</Typography>
        </Tooltip>
      )}
    </Stack>
  );
};
